import { Text } from '#app/components/Primitives/Text.js';
import { Tooltip } from '#app/components/Tooltip/Tooltip.js';
import { type Theme } from '#app/theme/index.js';
import {
  type TextInputSize,
  type TextInputValue,
  type TextInputVariant,
} from './types.js';
import {
  AddIcon,
  ChevronUpIcon,
  DividerIcon,
  type IconSvgProps,
  ReadOnlyIcon,
} from '@contra/icons/icons';
import { css, styled, useTheme } from 'styled-components';

const Stepper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
`;
const StepperDivider = styled.div`
  ${({ theme }) => css`
    height: 24px;
    width: 0;
    border-right: 1px solid ${theme.colorsV2.gray40};
  `}
`;
const MinusIcon = styled(DividerIcon)`
  ${({ theme }) => css`
    cursor: pointer;

    &:hover {
      color: ${theme.colorsV2.gray60};
    }
    transition: color 0.2s ease-out;
  `}
`;
const PlusIcon = styled(AddIcon)`
  ${({ theme }) => css`
    cursor: pointer;

    &:hover {
      color: ${theme.colorsV2.gray60};
    }
    transition: color 0.2s ease-out;
  `}
`;

type ChevronIconProps = {
  $disabled: boolean;
  $isOpen: boolean;
};
const ChevronIcon = styled(ChevronUpIcon)<ChevronIconProps>`
  ${({ $disabled, $isOpen, theme }) => css`
    transform: ${$isOpen ? 'rotate(0deg)' : 'rotate(180deg)'};
    transition:
      transform 0.2s ease-out,
      color 0.2s ease-out;

    ${$disabled
      ? css`
          pointer: default;
          color: ${theme.colorsV2.gray40};
        `
      : css`
          cursor: pointer;
          &:hover {
            color: ${theme.colorsV2.gray60};
          }
        `}
  `}
`;

const ClickableIconContainer = styled.div`
  ${({ theme }) => css`
    cursor: pointer;

    > svg {
      &:hover {
        color: ${theme.colorsV2.black};
      }
      transition: color 0.2s ease-out;
    }
  `}
`;

const getColor = ({
  disabled,
  theme,
}: {
  disabled: boolean;
  theme: Theme;
}): string => {
  if (disabled) {
    return theme.colorsV2.gray40;
  }

  return theme.colorsV2.gray60;
};

type SuffixProps<TVariant extends TextInputVariant> = {
  readonly disabled: boolean;
  readonly hideNumberIncrementMenu?: TVariant extends 'number'
    ? boolean
    : never;
  readonly isMenuOpen?: TVariant extends 'menu' ? boolean : never;
  readonly max?: number;
  readonly min?: TVariant extends 'number'
    ? number
    : TVariant extends 'date'
      ? string
      : never;
  readonly onValueChange?: (value?: TextInputValue<TVariant>) => void;
  readonly readOnly: boolean;
  readonly size: TextInputSize;
  readonly suffix?:
    | string
    | {
        customSize?: TextInputSize;
        icon: React.FunctionComponent<IconSvgProps>;
        label: string;
        onClick?: () => void;
        tooltip?: string;
      };
  readonly value?: TextInputValue<TVariant>;
  readonly variant: TVariant;
};

export const Suffix = <TVariant extends TextInputVariant>({
  disabled,
  hideNumberIncrementMenu,
  isMenuOpen,
  max,
  min,
  onValueChange,
  readOnly,
  size: defaultSize,
  suffix,
  value,
  variant,
}: SuffixProps<TVariant>) => {
  const theme = useTheme();
  const size =
    typeof suffix !== 'string' && suffix?.customSize
      ? suffix.customSize
      : defaultSize;

  if (variant === 'percent') {
    return (
      <Text
        color={getColor({ disabled, theme })}
        textStyle="subtitleSmall"
      >
        %
      </Text>
    );
  }

  if (
    variant === 'number' &&
    !readOnly &&
    !disabled &&
    !hideNumberIncrementMenu
  ) {
    return (
      <Stepper>
        <MinusIcon
          color={theme.colorsV2.black}
          onClick={() => {
            if (
              onValueChange &&
              (min === undefined ||
                (typeof min === 'number' && Number(value ?? 0) > min))
            ) {
              onValueChange(
                (Number(value ?? 0) - 1) as TextInputValue<TVariant>,
              );
            }
          }}
          size={16}
        />
        <StepperDivider />
        <PlusIcon
          color={theme.colorsV2.black}
          onClick={() => {
            if (
              onValueChange &&
              (max === undefined || Number(value ?? 0) < max)
            ) {
              onValueChange(
                (Number(value ?? 0) + 1) as TextInputValue<TVariant>,
              );
            }
          }}
          size={16}
        />
      </Stepper>
    );
  }

  if (variant === 'menu') {
    return (
      <ChevronIcon
        $disabled={disabled}
        $isOpen={isMenuOpen ?? false}
        size={size === 'small' ? 16 : 24}
      />
    );
  }

  if (suffix) {
    if (typeof suffix === 'string') {
      return (
        <Text
          color={getColor({ disabled, theme })}
          fontWeight={600}
          textStyle="subtitleSmall"
        >
          {suffix}
        </Text>
      );
    }

    const { icon: Icon, label, onClick, tooltip } = suffix;

    if (tooltip) {
      return (
        <Tooltip content={tooltip}>
          <Icon
            aria-label={label}
            color={getColor({ disabled, theme })}
            size={size === 'small' ? 16 : 24}
          />
        </Tooltip>
      );
    }

    const iconProps = {
      'aria-label': label,
      color: getColor({ disabled, theme }),
      size: size === 'small' ? 16 : 24,
    };

    if (onClick) {
      return (
        <ClickableIconContainer>
          <Icon
            {...iconProps}
            onClick={onClick}
          />
        </ClickableIconContainer>
      );
    }

    return <Icon {...iconProps} />;
  }

  if (readOnly) {
    return (
      <ReadOnlyIcon
        color={getColor({ disabled: false, theme })}
        size={16}
      />
    );
  }

  return null;
};
